/* eslint-disable @typescript-eslint/no-empty-object-type */
import { useEffect, useMemo, useState, memo, useCallback } from 'react';
import { Checkbox } from '@/components/_shadcn-ui/checkbox';
import { cn } from '@/utils/cn';
import { useForm } from 'react-hook-form';
import {
  bulkAssignLabelCaseValidationSchema,
  TBulkAssignLabelCaseValidation,
  useBulkAssignLabelCaseMutation,
} from '../../../api/bulk-assign-label-case.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleDomBubbleEvent } from '@/utils/dom';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { SearchIcon } from 'lucide-react';
import { useGetListLabelCaseQuery } from '../../../api/get-list-label-case.api';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { TLabelCase } from '../../../types/label-case.type';
import { createBulkAssignLabelCaseOptimisticUpdate } from './bulk-assign-label-case-optimistic-update';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import { Input } from '@/components/_ui/input';
import useSelectedCaseQs from '@/features/cases/case/hooks/use-case-qs';
import { useCasesActions, useSelectedCases, useSelectedMenuCaseMode } from '@/features/cases/case/stores/cases.store';

// Type definition for case label structure
type CaseLabel = {
  labelCaseId: string;
  name: string;
  color: string;
};

export type TBulkAssignLabelCaseModalType = 'BulkAssignLabelCaseModal';
export interface IBulkAssignLabelCaseModalProps {}

/**
 * BulkAssignLabelCaseModal Component
 *
 * This modal allows users to assign case labels to multiple cases at once.
 * Features:
 * - Search through available case labels
 * - Select/deselect multiple labels with checkboxes
 * - Visual color indicators for each label
 * - Pre-populated with existing labels when editing
 */
const BulkAssignLabelCaseModal = memo((_props: IBulkAssignLabelCaseModalProps) => {
  const { handleSelectedCase } = useSelectedCaseQs();
  const showToast = useCustomToast();
  const selectedCaseMode = useSelectedMenuCaseMode();
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const { setResetSelectedCases, setResetSelectedMenuCaseMode, setResetSelectedCase } = useCasesActions();
  const selectedCases = useSelectedCases();

  const form = useForm<TBulkAssignLabelCaseValidation>({
    mode: 'onChange',
    resolver: zodResolver(bulkAssignLabelCaseValidationSchema),
    defaultValues: {
      caseIds: [],
      labelIds: [],
    },
  });

  // Check if modal is open
  const isOpenBulkAssignLabelCaseModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('BulkAssignLabelCaseModal');
  }, [caseModuleModalRootActions]);

  // Local state for search functionality and selected labels
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedLabelCases, setSelectedLabelCases] = useState<TLabelCase['labelCaseId'][]>([]);

  // Initialize selected labels when modal opens with default values
  useEffect(() => {
    if (selectedCases?.length) {
      form.reset({
        caseIds: selectedCases?.map((c) => c.caseId) ?? [],
        labelIds: [],
      });
    }
  }, [selectedCases, form]);

  // Fetch all available case labels from API
  const { data: labelCaseData, isLoading: _isLoadingLabelCase } = useGetListLabelCaseQuery({
    params: {
      page: 1,
      limit: 500, // Fetch a large number to show all available labels
      search: '',
      sortBy: 'createdAt',
      sortDir: 'DESC',
    },
    queryConfig: {
      enabled: isOpenBulkAssignLabelCaseModal,
    },
  });

  // Extract label options from API response
  const availableLabelOptions = useMemo(() => {
    return (
      labelCaseData?.payload?.rows?.filter((item: TLabelCase) =>
        item?.name?.toLowerCase().includes(searchQuery?.toLowerCase())
      ) || []
    );
  }, [labelCaseData, searchQuery]);

  /**
   * Handle selection/deselection of a case label
   * @param labelToToggle - The case label to toggle selection for
   */
  const handleLabelSelectionToggle = useCallback(
    (labelToToggle: CaseLabel) => {
      const isLabelCurrentlySelected = selectedLabelCases.some(
        (selectedLabelCaseId) => selectedLabelCaseId === labelToToggle.labelCaseId
      );

      if (isLabelCurrentlySelected) {
        // Remove label from selection if already selected
        const updatedSelection = selectedLabelCases.filter(
          (selectedLabelCaseId) => selectedLabelCaseId !== labelToToggle.labelCaseId
        );
        form.setValue('labelIds', updatedSelection);
        setSelectedLabelCases(updatedSelection);
      } else {
        // Add label to selection if not currently selected
        const updatedSelection = [...selectedLabelCases, labelToToggle.labelCaseId];
        form.setValue('labelIds', updatedSelection);
        setSelectedLabelCases(updatedSelection);
      }
    },
    [selectedLabelCases, form]
  );

  /**
   * Check if a specific label is currently selected
   * @param labelToCheck - The case label to check selection status for
   * @returns boolean indicating if the label is selected
   */
  const isLabelSelected = useCallback(
    (labelToCheck: CaseLabel): boolean => {
      return selectedLabelCases.some((selectedLabelId) => selectedLabelId === labelToCheck.labelCaseId);
    },
    [selectedLabelCases]
  );

  /**
   * Check if all available labels are currently selected
   * @returns boolean indicating if all labels are selected
   */
  const isAllSelected = useCallback((): boolean => {
    return availableLabelOptions.length > 0 && selectedLabelCases.length === availableLabelOptions.length;
  }, [availableLabelOptions, selectedLabelCases]);

  /**
   * Handle selection/deselection of all labels
   */
  const handleLabelSelectionAllToggle = useCallback(() => {
    if (isAllSelected()) {
      // If all are selected, deselect all
      form.setValue('labelIds', []);
      setSelectedLabelCases([]);
    } else {
      // If not all are selected, select all
      const allLabelIds = availableLabelOptions.map((label: TLabelCase) => label.labelCaseId);
      form.setValue('labelIds', allLabelIds);
      setSelectedLabelCases(allLabelIds);
    }
  }, [isAllSelected, availableLabelOptions, form]);

  // Get all available labels (not filtered) for mapping labelIds to full objects
  const allAvailableLabels = useMemo(() => {
    return labelCaseData?.payload?.rows || [];
  }, [labelCaseData]);

  // Create optimistic update handlers
  const optimisticUpdateHandlers = useMemo(
    () =>
      createBulkAssignLabelCaseOptimisticUpdate({
        allAvailableLabels,
      }),
    [allAvailableLabels]
  );

  const mutationBulkAssignLabelCase = useBulkAssignLabelCaseMutation({
    mutationConfigOptions: {
      onMutate: optimisticUpdateHandlers.onMutate,
      onError: (error, variables, context) => {
        const { errorMessage } = optimisticUpdateHandlers.onError(error, variables, context);
        showToast({
          type: 'error',
          message: errorMessage,
        });
      },
      onSuccess: () => {
        handleSelectedCase(null);
        setResetSelectedCases();
        setResetSelectedCase();
        if (selectedCaseMode !== 'idle') {
          setResetSelectedMenuCaseMode();
        }
        optimisticUpdateHandlers.onSuccess();
        form.reset();
        setSelectedLabelCases([]);
        caseModuleModalRootActions.closeModal('BulkAssignLabelCaseModal');
        showToast({
          type: 'success',
          message: 'Case labels assigned successfully',
        });
      },
    },
  });

  const handleSubmitBulkAssignLabelCase = useCallback(
    (data: TBulkAssignLabelCaseValidation) => {
      mutationBulkAssignLabelCase.mutate({
        caseIds: data.caseIds,
        labelIds: data.labelIds,
      });
    },
    [mutationBulkAssignLabelCase]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('BulkAssignLabelCaseModal');
    form.reset();
    setSelectedLabelCases([]);
  }, [caseModuleModalRootActions, form]);

  return (
    <Modal
      isOpen={isOpenBulkAssignLabelCaseModal}
      lockBodyScroll
      onClose={handleClose}
      isCloseOutside={false}
      className="w-[400px]"
    >
      <ModalHeader>
        <ModalTitle>Bulk Select Label Case</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="flex flex-col gap-[12px]">
          {/* Search input for filtering labels */}
          <div>
            <Input
              startIcon={<SearchIcon className="w-[16px] h-[16px]" />}
              placeholder="Search label case..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>

          {/* Scrollable container for label options */}
          <div className="flex flex-col border border-[#D9D9D9] rounded-[8px] overflow-hidden">
            {/* Sticky "Select All" header */}
            <div className="flex items-center justify-between gap-[20px] bg-[#0C8DBF] px-[16px] py-[12px] sticky top-0 z-10">
              <div className="flex items-center gap-[16px]">
                <Checkbox
                  className="border-white bg-white outline-hidden  data-[state=checked]:text-[#0C8DBF] data-[state=checked]:bg-white data-[state=checked]:border-[#0C8DBF] data-[state=unchecked]:text-white data-[state=unchecked]:bg-white data-[state=unchecked]:border-[#D9D9D9]"
                  checked={isAllSelected()}
                  onCheckedChange={handleLabelSelectionAllToggle}
                />
                <span className="text-[12px] font-normal text-white">Select All</span>
              </div>
              <div className="flex items-center gap-[12px]">
                <span className="text-[12px] font-normal text-white">
                  {selectedLabelCases.length} / {availableLabelOptions.length}
                </span>
              </div>
            </div>

            {/* Scrollable content area */}
            <div className="max-h-[200px] overflow-y-auto">
              {availableLabelOptions.map((labelOption: TLabelCase) => {
                const isCurrentlySelected = isLabelSelected(labelOption);

                return (
                  <div
                    key={labelOption.labelCaseId}
                    className="flex items-center gap-[20px] cursor-pointer hover:bg-gray-50   transition-colors px-[16px] py-[12px] border-b border-[#D9D9D9]"
                    onClick={() => handleLabelSelectionToggle(labelOption)}
                  >
                    {/* Checkbox to show selection state */}
                    <Checkbox
                      checked={isCurrentlySelected}
                      // Note: Selection is handled via parent div onClick, not checkbox onChange
                    />

                    {/* Color indicator for the label */}
                    <div
                      className={cn(`w-[12px] h-[12px] rounded-full border border-[#${labelOption.color}]`)}
                      style={{ backgroundColor: labelOption.color }}
                    />

                    {/* Label name */}
                    <span className="text-[12px] font-normal flex-1">{labelOption.name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <form
            id="bulk-assign-label-case-form"
            onSubmit={(e) => {
              handleDomBubbleEvent(e);
              form.handleSubmit(handleSubmitBulkAssignLabelCase)(e);
            }}
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          form="bulk-assign-label-case-form"
          type="submit"
          disabled={mutationBulkAssignLabelCase.isPending}
        >
          {mutationBulkAssignLabelCase.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}{' '}
          Assign
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

BulkAssignLabelCaseModal.displayName = 'BulkAssignLabelCaseModal';
export default BulkAssignLabelCaseModal;
